// src/components/canvasRecorder/components/CanvasBackground.tsx
import React, { useEffect, useRef } from "react";
import * as fabric from "fabric";
import useRecorderContext from "../context/useRecorderContext";
import logger from "../utils/logger";

type FabricLinearGradient = fabric.Gradient<"linear">;
type FabricRadialGradient = fabric.Gradient<"radial">;
type FabricAnyGradient = FabricLinearGradient | FabricRadialGradient;

const CanvasBackground: React.FC = () => {
  const {
    canvasWidth,
    canvasHeight,
    backgroundColor,
    backgroundImage,
    stageRef,
    canvasReady,
    isRecording,
  } = useRecorderContext();

  const backgroundRectRef = useRef<fabric.Rect | null>(null);
  const backgroundImageRef = useRef<fabric.Image | null>(null);
  const pendingImageRef = useRef<HTMLImageElement | null>(null);
  const appliedImageDimsRef = useRef<{ w: number; h: number } | null>(null);
  
  // Track if component is mounted to distinguish unmount from re-render
  const isMountedRef = useRef(true);
  
  // Set mounted on mount, clear on unmount
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);
  
  // ---------------------------------------------------------
  // Utilities
  // ---------------------------------------------------------

  const cleanupBackgroundObjects = (canvas: fabric.Canvas, _reason?: string) => {
    if (backgroundRectRef.current) {
      canvas.remove(backgroundRectRef.current);
      backgroundRectRef.current = null;
    }
    if (backgroundImageRef.current) {
      canvas.remove(backgroundImageRef.current);
      backgroundImageRef.current = null;
      appliedImageDimsRef.current = null;
    }
    if (pendingImageRef.current) {
      pendingImageRef.current.onload = null;
      pendingImageRef.current.onerror = null;
      pendingImageRef.current.src = "";
      pendingImageRef.current = null;
    }
    const objects = canvas.getObjects();
    const baseRects = objects.filter((o: any) => o.name === "background-base");
    baseRects.forEach((r: any) => canvas.remove(r));
  };

  function isGradient(value: string): boolean {
    return (
      typeof value === "string" &&
      /\b(linear-gradient|radial-gradient)\(/i.test(value)
    );
  }

  // Split only on commas that are NOT inside parentheses
  function splitTopLevelComma(s: string): string[] {
    const out: string[] = [];
    let depth = 0,
      last = 0;
    for (let i = 0; i < s.length; i++) {
      const ch = s[i];
      if (ch === "(") depth++;
      else if (ch === ")") depth = Math.max(0, depth - 1);
      else if (ch === "," && depth === 0) {
        out.push(s.slice(last, i).trim());
        last = i + 1;
      }
    }
    out.push(s.slice(last).trim());
    return out.filter(Boolean);
  }

  // Parse a list of color stops: "rgba(...)[ offset%]"
  function parseColorStopsList(
    stopsStr: string
  ): { color: string; offset: number | null }[] {
    const parts = splitTopLevelComma(stopsStr);
    const stops = parts
      .map((p) => {
        const color = (p.match(/(?:rgba?\([^)]+\)|#[0-9a-fA-F]{3,8})/) || [
          null,
        ])[0];
        const off = (p.match(/(-?\d+(?:\.\d+)?)%/) || [null, null])[1];
        return color
          ? { color, offset: off != null ? parseFloat(off) / 100 : null }
          : null;
      })
      .filter(Boolean) as { color: string; offset: number | null }[];

    // Normalize & sort
    if (stops.length) {
      if (stops[0].offset == null) stops[0].offset = 0;
      if (stops[stops.length - 1].offset == null)
        stops[stops.length - 1].offset = 1;

      // Interpolate missing in-between offsets
      let anchor = stops[0].offset!;
      let ai = 0;
      for (let i = 1; i < stops.length; i++) {
        if (stops[i].offset != null) {
          const span = i - ai;
          const step = (stops[i].offset! - anchor) / span;
          for (let k = 1; k < span; k++)
            stops[ai + k].offset = anchor + step * k;
          anchor = stops[i].offset!;
          ai = i;
        }
      }

      // Clamp and sort just in case the input was unordered
      for (const s of stops) s.offset = Math.max(0, Math.min(1, s.offset!));
      stops.sort((a, b) => a.offset! - b.offset!);
    }

    return stops;
  }

  // CSS angle (0deg=top, 90deg=right) → percentage coords spanning the rect
  function linearAngleToCoords(angleDeg: number) {
    const rad = ((90 - angleDeg) * Math.PI) / 180;
    const vx = Math.cos(rad),
      vy = Math.sin(rad);
    const half = 0.70710678; // √2/2 to cover fully along diagonal
    const cx = 0.5,
      cy = 0.5;
    return {
      x1: cx - vx * half,
      y1: cy - vy * half,
      x2: cx + vx * half,
      y2: cy + vy * half,
    };
  }

  // header: "circle 382px at 10% 20%", "circle farthest-corner at -13.6% 51.7%", etc.
  function parseRadialHeaderPx(header: string, W: number, H: number) {
    let cx = 0.5 * W,
      cy = 0.5 * H;

    const at = header.match(/\bat\s+([-\d.]+%?)\s+([-\d.]+%?)\b/i);
    if (at) {
      const [, ax, ay] = at;
      cx = ax.endsWith("%") ? (parseFloat(ax) / 100) * W : parseFloat(ax);
      cy = ay.endsWith("%") ? (parseFloat(ay) / 100) * H : parseFloat(ay);
      if (Number.isNaN(cx)) cx = 0.5 * W;
      if (Number.isNaN(cy)) cy = 0.5 * H;
    }

    // Default: farthest-corner from (cx,cy)
    const farthestCorner = (() => {
      const d1 = Math.hypot(0 - cx, 0 - cy);
      const d2 = Math.hypot(W - cx, 0 - cy);
      const d3 = Math.hypot(0 - cx, H - cy);
      const d4 = Math.hypot(W - cx, H - cy);
      return Math.max(d1, d2, d3, d4);
    })();

    let r = farthestCorner;

    // Explicit px radius
    const rx = header.match(/circle\s+([-\d.]+)px/i);
    if (rx) {
      const rPx = parseFloat(rx[1]);
      if (!Number.isNaN(rPx)) r = rPx;
    } else if (/farthest-corner/i.test(header)) {
      r = farthestCorner;
    }

    return { cx, cy, r };
  }

  function buildFabricGradientFromCss(
    value: string,
    W: number,
    H: number
  ): FabricAnyGradient | string {
    const m = value.match(/(linear-gradient|radial-gradient)\((.*)\)$/i);
    if (!m) return value;

    const kind = m[1].toLowerCase();
    const inside = m[2];

    // ----------------------- LINEAR -----------------------
    if (kind === "linear-gradient") {
      const tokens = splitTopLevelComma(inside);
      let angle = 180; // CSS default
      const angleMatch = tokens[0]?.match(/(-?\d+(?:\.\d+)?)deg/);
      if (angleMatch) {
        angle = parseFloat(angleMatch[1]);
        tokens.shift(); // remove angle token
      }

      const colorStops = parseColorStopsList(tokens.join(", "));
      if (colorStops.length < 2) return value;

      const { x1, y1, x2, y2 } = linearAngleToCoords(angle);
      return new fabric.Gradient<"linear">({
        type: "linear",
        gradientUnits: "percentage",
        coords: { x1, y1, x2, y2 },
        colorStops: colorStops.map((s) => ({
          offset: s.offset!,
          color: s.color,
        })),
      });
    }

    // ----------------------- RADIAL -----------------------
    if (kind === "radial-gradient") {
      const parts = splitTopLevelComma(inside);
      const header = parts.shift() ?? "";
      const colorStops = parseColorStopsList(parts.join(", "));
      if (colorStops.length < 2) return value;

      const { cx, cy, r } = parseRadialHeaderPx(header, W, H);

      return new fabric.Gradient<"radial">({
        type: "radial",
        gradientUnits: "pixels",
        coords: { x1: cx, y1: cy, r1: 0, x2: cx, y2: cy, r2: r },
        colorStops: colorStops.map((s) => ({
          offset: s.offset!,
          color: s.color,
        })),
      });
    }

    return value;
  }

  // ---------------------------------------------------------
  // Effect: build / update background
  // ---------------------------------------------------------
  useEffect(() => {
    if (!canvasReady || !stageRef?.current) return;

    const canvas = stageRef.current.getCanvas();
    if (!canvas) return;

    // When both background states are cleared (e.g. project switch), clean up canvas
    if (!backgroundColor && !backgroundImage) {
      cleanupBackgroundObjects(canvas, 'background state cleared');
      stageRef?.current?.requestRender();
      return;
    }

    // CRITICAL FIX: For image backgrounds, DON'T cleanup until new image is loaded
    // This prevents a blank flash during async image loading
    // Only cleanup immediately for color/gradient backgrounds (which are sync)
    
    // Color or gradient (sync - can cleanup immediately)
    if (backgroundColor) {
      cleanupBackgroundObjects(canvas, 'color/gradient rebuild');
      
      let fill: string | FabricAnyGradient | undefined;
      const isGrad = isGradient(backgroundColor);
      if (isGrad) {
        fill = buildFabricGradientFromCss(
          backgroundColor,
          canvasWidth,
          canvasHeight
        );
      } else {
        fill = backgroundColor;
      }

      if (isGrad) {
        const blackRect = new fabric.Rect({
          left: 0,
          top: 0,
          width: canvasWidth,
          height: canvasHeight,
          fill: "#000000",
          selectable: false,
          evented: false,
          name: "background-base",
        });
        canvas.add(blackRect);
        canvas.sendObjectToBack(blackRect);
      }

      const rect = new fabric.Rect({
        left: 0,
        top: 0,
        width: canvasWidth,
        height: canvasHeight,
        fill,
        selectable: false,
        evented: false,
        name: "background",
      });

      backgroundRectRef.current = rect;
      canvas.add(rect);
      if (isGrad) {
        canvas.sendObjectToBack(rect);
        const objects = canvas.getObjects();
        const blackBase = objects.find((o: any) => o.name === "background-base");
        if (blackBase) canvas.sendObjectToBack(blackBase);
      } else {
        canvas.sendObjectToBack(rect);
      }
    }

    // Image - use the already-loaded backgroundImage element directly
    if (backgroundImage && backgroundImage.src) {
      const currentFabricImage = backgroundImageRef.current;
      const currentSrc = (currentFabricImage?.getElement() as HTMLImageElement)?.src;
      const sameSrc = currentSrc === backgroundImage.src;
      const sameDims = appliedImageDimsRef.current
        && appliedImageDimsRef.current.w === canvasWidth
        && appliedImageDimsRef.current.h === canvasHeight;

      if (sameSrc && sameDims) {
        return;
      }

      if (isRecording) {
        return;
      }

      // Remove old color/gradient rects when switching to image
      if (!backgroundColor) {
        if (backgroundRectRef.current) {
          canvas.remove(backgroundRectRef.current);
          backgroundRectRef.current = null;
        }
        const baseRects = canvas.getObjects().filter((o: any) => o.name === "background-base");
        baseRects.forEach((r: any) => canvas.remove(r));
      }

      const applyImage = (img: HTMLImageElement) => {
        if (!canvas) return;

        if (backgroundImageRef.current) {
          canvas.remove(backgroundImageRef.current);
          backgroundImageRef.current = null;
        }
        if (backgroundRectRef.current) {
          canvas.remove(backgroundRectRef.current);
          backgroundRectRef.current = null;
        }

        const canvasAspect = canvasWidth / canvasHeight;
        const imageAspect = img.naturalWidth / img.naturalHeight;

        let scaleX, scaleY, left, top;
        if (imageAspect > canvasAspect) {
          scaleY = canvasHeight / img.naturalHeight;
          scaleX = scaleY;
          left = (canvasWidth - img.naturalWidth * scaleX) / 2;
          top = 0;
        } else {
          scaleX = canvasWidth / img.naturalWidth;
          scaleY = scaleX;
          left = 0;
          top = (canvasHeight - img.naturalHeight * scaleY) / 2;
        }

        const fabricImage = new fabric.Image(img, {
          left,
          top,
          scaleX,
          scaleY,
          selectable: false,
          evented: false,
          name: "background",
        });

        backgroundImageRef.current = fabricImage;
        appliedImageDimsRef.current = { w: canvasWidth, h: canvasHeight };
        canvas.add(fabricImage);
        canvas.sendObjectToBack(fabricImage);

        if (backgroundRectRef.current) {
          canvas.sendObjectToBack(fabricImage);
          canvas.sendObjectToBack(backgroundRectRef.current);
        }

        stageRef?.current?.requestRender();
      };

      if (backgroundImage.naturalWidth > 0 && backgroundImage.naturalHeight > 0) {
        applyImage(backgroundImage);
      } else {
        pendingImageRef.current = backgroundImage;
        backgroundImage.onload = () => {
          if (!canvas || isRecording) {
            pendingImageRef.current = null;
            return;
          }
          pendingImageRef.current = null;
          applyImage(backgroundImage);
        };
        backgroundImage.onerror = (error) => {
          pendingImageRef.current = null;
          logger.error(
            "CanvasBackground: Failed to load background image",
            error
          );
        };
      }
    }

    stageRef?.current?.requestRender();

    // CRITICAL FIX: Only cleanup on actual component unmount, NOT on dependency changes
    // The cleanup for dependency changes is handled:
    // - For color/gradient: at the start of the effect body (sync)
    // - For image: inside img.onload after new image loads (prevents blank flash)
    // The return cleanup was running on every re-render, removing the background
    // before the new one could load, causing the blank flash bug.
    return () => {
      // Only cleanup if component is actually unmounting
      // Use setTimeout(0) to check after React has finished its update cycle
      setTimeout(() => {
        if (!isMountedRef.current && canvas) {
          cleanupBackgroundObjects(canvas, 'component unmount');
        }
      }, 0);
    };
  }, [
    stageRef,
    canvasWidth,
    canvasHeight,
    backgroundColor,
    backgroundImage,
    canvasReady,
    // recordingQuality removed - background doesn't need to rebuild on quality change
  ]);

  return null;
};

export default CanvasBackground;
